import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SideMenu from '../components/SideMenu';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useEnvironment } from '../context/EnvironmentContext';
import { usePricingRules } from '../context/PricingRulesContext';
import './webhooks.css';

const PAGE_SIZE = 10;

const EMPTY_FORM = {
  environment: 'TEST',
  provider_code: '',
  service_code: '',
  delivery_type: '',
  currency: 'EUR',
  markup_percent: '',
  markup_fixed: '',
  min_margin: '',
  rounding_mode: 'UP',
  rounding_precision: '1',
  vat_rate_percent: '',
  tax_rate_percent: '',
  tax_included: true,
  priority: '0',
  is_active: true,
  conditions_text: '',
};

function resolveCompanyId(user) {
  const roles = Array.isArray(user?.roles) ? user.roles : [];
  if (roles.includes('administrator')) {
    return (
      user?.activeCompany?.id ??
      user?.activeCompany?.company_id ??
      user?.activeCompany?.companyId ??
      user?.myCompany?.id ??
      user?.myCompany?.company_id ??
      user?.myCompany?.companyId ??
      ''
    );
  }
  return (
    user?.myCompany?.id ??
    user?.myCompany?.company_id ??
    user?.myCompany?.companyId ??
    user?.company?.id ??
    ''
  );
}

function toNumberOrNull(value) {
  if (value === '' || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function formatMarkup(rule) {
  const parts = [];
  if (rule?.markup_percent != null && rule.markup_percent !== '') {
    parts.push(`+${rule.markup_percent}%`);
  }
  if (rule?.markup_fixed != null && rule.markup_fixed !== '') {
    parts.push(`+${rule.markup_fixed}`);
  }
  return parts.length > 0 ? parts.join(' ') : '—';
}

function formatRounding(rule) {
  const mode = rule?.rounding_mode;
  const precision = rule?.rounding_precision;
  if (!mode && precision == null) return '—';
  return `${mode || '—'} / ${precision ?? '—'}`;
}

function formatVatTax(rule) {
  const vat = rule?.vat_rate_percent;
  const tax = rule?.tax_rate_percent;
  if (vat == null && tax == null) return '—';
  const parts = [];
  if (vat != null) parts.push(`VAT ${vat}%`);
  if (tax != null) parts.push(`Tax ${tax}%`);
  return parts.join(', ');
}

function ruleToForm(rule) {
  if (!rule) return { ...EMPTY_FORM };
  return {
    environment: rule.environment || 'TEST',
    provider_code: rule.provider_code || '',
    service_code: rule.service_code || '',
    delivery_type: rule.delivery_type || '',
    currency: rule.currency || 'EUR',
    markup_percent: rule.markup_percent ?? '',
    markup_fixed: rule.markup_fixed ?? '',
    min_margin: rule.min_margin ?? '',
    rounding_mode: rule.rounding_mode || 'UP',
    rounding_precision: rule.rounding_precision ?? '1',
    vat_rate_percent: rule.vat_rate_percent ?? '',
    tax_rate_percent: rule.tax_rate_percent ?? '',
    tax_included: rule.tax_included !== false,
    priority: rule.priority ?? '0',
    is_active: rule.is_active !== false,
    conditions_text:
      rule.conditions == null
        ? ''
        : typeof rule.conditions === 'string'
          ? rule.conditions
          : JSON.stringify(rule.conditions, null, 2),
  };
}

export default function PricingRules() {
  const { currentTheme, theme } = useTheme();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { environment } = useEnvironment();
  const {
    rules,
    refreshRules,
    createRule,
    updateRule,
    deleteRule,
    loading,
    saving,
    error,
  } = usePricingRules();

  const roles = Array.isArray(user?.roles) ? user.roles : [];
  const isAdmin = roles.includes('administrator');

  const [editing, setEditing] = useState(null);
  const [editMode, setEditMode] = useState('edit');
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [page, setPage] = useState(1);

  useEffect(() => {
    refreshRules({ environment });
  }, [refreshRules, environment]);

  const rows = useMemo(() => {
    return (Array.isArray(rules) ? rules : []).map((rule, idx) => ({
      id: rule?.id ?? `${idx}`,
      raw: rule,
    }));
  }, [rules]);
  const totalRows = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const pagedRows = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return rows.slice(start, start + PAGE_SIZE);
  }, [rows, safePage]);

  useEffect(() => {
    if (page !== safePage) {
      setPage(safePage);
    }
  }, [page, safePage]);

  const canSaveForm = useMemo(() => {
    return Boolean(form.provider_code.trim()) && Boolean(form.currency.trim());
  }, [form.provider_code, form.currency]);

  const openCreate = () => {
    setEditMode('create');
    setEditing({ id: null });
    setForm({ ...EMPTY_FORM, environment });
  };

  const openEdit = (row) => {
    setEditMode('edit');
    setEditing(row);
    setForm(ruleToForm(row.raw));
  };

  const closeEdit = () => {
    setEditing(null);
  };

  const buildPayload = () => {
    let conditions = null;
    const trimmedConditions = String(form.conditions_text || '').trim();
    if (trimmedConditions) {
      conditions = JSON.parse(trimmedConditions);
    }

    return {
      environment: form.environment || environment,
      provider_code: form.provider_code.trim() || null,
      service_code: form.service_code.trim() || null,
      delivery_type: form.delivery_type.trim() || null,
      currency: form.currency.trim(),
      markup_percent: toNumberOrNull(form.markup_percent),
      markup_fixed: toNumberOrNull(form.markup_fixed),
      min_margin: toNumberOrNull(form.min_margin),
      rounding_mode: form.rounding_mode.trim() || null,
      rounding_precision: toNumberOrNull(form.rounding_precision),
      vat_rate_percent: toNumberOrNull(form.vat_rate_percent),
      tax_rate_percent: toNumberOrNull(form.tax_rate_percent),
      tax_included: Boolean(form.tax_included),
      priority: toNumberOrNull(form.priority) ?? 0,
      is_active: Boolean(form.is_active),
      conditions,
    };
  };

  const onSave = async () => {
    if (!canSaveForm) {
      window.alert(t('pricingRulesRequiredFields'));
      return;
    }

    try {
      const payload = buildPayload();
      if (editMode === 'create') {
        const companyId = resolveCompanyId(user);
        if (!companyId) {
          window.alert(t('pricingRulesMissingCompany'));
          return;
        }
        const res = await createRule({ ...payload, company_id: companyId });
        if (res?.ok) {
          closeEdit();
          setPage(1);
          await refreshRules({ environment });
        } else if (res?.detail) {
          window.alert(`${t('pricingRulesSaveFailed')}: ${res.detail}`);
        }
        return;
      }

      const res = await updateRule(editing.id, {
        ...payload,
        company_id: editing.raw?.company_id,
      });
      if (res?.ok) {
        closeEdit();
        await refreshRules({ environment });
      }
    } catch (err) {
      console.error('Invalid pricing rule form:', err);
      window.alert(t('pricingRulesInvalidJson'));
    }
  };

  const onRemove = async () => {
    if (editMode !== 'edit' || !editing?.id) return;
    if (!window.confirm(t('confirmRemovePricingRule'))) return;
    const res = await deleteRule(editing.id);
    if (res?.ok) {
      closeEdit();
      const nextPage = pagedRows.length <= 1 && safePage > 1 ? safePage - 1 : safePage;
      setPage(nextPage);
      await refreshRules({ environment });
    }
  };

  return (
    <div
      className="webhooks-page"
      style={{
        '--wh-background': currentTheme.background,
        '--wh-surface': currentTheme.surface,
        '--wh-border': currentTheme.border,
        '--wh-card-shadow': currentTheme.cardShadow,
        '--wh-text': currentTheme.text,
        '--wh-text-secondary': currentTheme.textSecondary,
      }}
    >
      <SideMenu currentTheme={currentTheme} theme={theme} />

      <main className="webhooks-main">
        <h1 className="webhooks-title">{t('pricingRules')}</h1>

        {loading && (
          <div className="webhooks-muted">{t('loading')}</div>
        )}

        {!loading && error && (
          <div className={`webhooks-error ${theme}`}>{t(error)}</div>
        )}

        {!loading && (
          <div className="webhooks-tableWrap">
            <div className={`webhooks-tableTopbar ${theme}`}>
              <div className="webhooks-tableTopbarTitle">{t('pricingRulesPanel')}</div>
              {isAdmin && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button
                    type="button"
                    onClick={openCreate}
                    disabled={saving}
                    className={[
                      'webhooks-primaryBtn',
                      theme,
                      saving ? 'disabled' : 'enabled',
                    ].join(' ')}
                  >
                    {t('create')}
                  </button>
                  <button
                    type="button"
                    onClick={() => refreshRules({ environment })}
                    disabled={loading || saving}
                    className={[
                      'webhooks-secondaryBtn',
                      theme,
                      loading || saving ? 'disabled' : 'enabled',
                    ].join(' ')}
                  >
                    {loading ? `${t('loading')}...` : t('refresh')}
                  </button>
                </div>
              )}
            </div>

            {pagedRows.length > 0 ? (
              <table className="webhooks-table">
                <thead>
                  <tr>
                    <th className="webhooks-th">{t('Provider')}</th>
                    <th className="webhooks-th">{t('pricingService')}</th>
                    <th className="webhooks-th">{t('pricingDeliveryType')}</th>
                    <th className="webhooks-th">{t('pricingCurrency')}</th>
                    <th className="webhooks-th">{t('pricingMarkup')}</th>
                    <th className="webhooks-th">{t('pricingMinMargin')}</th>
                    <th className="webhooks-th">{t('pricingRounding')}</th>
                    <th className="webhooks-th">{t('pricingVatTax')}</th>
                    <th className="webhooks-th">{t('Status')}</th>
                    <th className="webhooks-th webhooks-thRight">{t('actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedRows.map((row, idx) => {
                    const rule = row.raw || {};
                    return (
                      <tr key={row.id} className={idx === pagedRows.length - 1 ? 'webhooks-trLast' : ''}>
                        <td className="webhooks-td">{rule.provider_code || '—'}</td>
                        <td className="webhooks-td">{rule.service_code || '—'}</td>
                        <td className="webhooks-td">{rule.delivery_type || '—'}</td>
                        <td className="webhooks-td">{rule.currency || '—'}</td>
                        <td className="webhooks-td">{formatMarkup(rule)}</td>
                        <td className="webhooks-td">{rule.min_margin ?? '—'}</td>
                        <td className="webhooks-td">{formatRounding(rule)}</td>
                        <td className="webhooks-td">{formatVatTax(rule)}</td>
                        <td className="webhooks-td">
                          <span
                            className={[
                              'webhooks-statusPill',
                              rule.is_active !== false ? 'active' : 'disabled',
                              theme,
                            ].join(' ')}
                          >
                            {rule.is_active !== false ? t('Active') : t('pricingInactive')}
                          </span>
                        </td>
                        <td className="webhooks-td webhooks-actionsCell">
                          <button
                            type="button"
                            onClick={() => openEdit(row)}
                            aria-label={t('pricingRuleEditTitle')}
                            title={t('pricingRuleEditTitle')}
                            className={['webhooks-iconBtn', theme].join(' ')}
                          >
                            <span className="oi oi-ellipses" aria-hidden="true" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="webhooks-muted webhooks-tableEmpty">
                {t('pricingRulesEmpty')}
              </div>
            )}
            {totalRows > 0 && (
              <div
                style={{
                  padding: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  borderTop: `1px solid ${currentTheme.border}`,
                  background: theme === 'light' ? '#F9FAFB' : 'rgba(255,255,255,0.04)',
                }}
              >
                <div style={{ fontSize: 12, color: currentTheme.textSecondary }}>
                  {t('Items per page')}: {PAGE_SIZE}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <button
                    type="button"
                    disabled={safePage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className={[
                      'webhooks-secondaryBtn',
                      theme,
                      safePage <= 1 ? 'disabled' : 'enabled',
                    ].join(' ')}
                  >
                    {t('Prev')}
                  </button>
                  <div style={{ fontSize: 12, color: currentTheme.textSecondary, whiteSpace: 'nowrap' }}>
                    {t('Page')} {safePage} {t('of')} {totalPages}
                  </div>
                  <button
                    type="button"
                    disabled={safePage >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className={[
                      'webhooks-secondaryBtn',
                      theme,
                      safePage >= totalPages ? 'disabled' : 'enabled',
                    ].join(' ')}
                  >
                    {t('Next')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {!!editing && (
          <div
            role="dialog"
            aria-modal="true"
            className={['webhooks-modalOverlay', theme].join(' ')}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) closeEdit();
            }}
          >
            <div className="webhooks-modal">
              <div className="webhooks-modalHeader">
                <div className="webhooks-modalTitle">
                  {editMode === 'create' ? t('pricingRuleCreateTitle') : t('pricingRuleEditTitle')}
                </div>
                <button
                  type="button"
                  onClick={closeEdit}
                  className={['webhooks-iconBtn', theme].join(' ')}
                  aria-label={t('Cancel')}
                  title={t('Cancel')}
                >
                  ×
                </button>
              </div>

              <div className="webhooks-modalBody">
              <div className="webhooks-formGrid">
                <label className="webhooks-label">
                  <div className="webhooks-fieldLabel">{t('Provider')}</div>
                  <input
                    value={form.provider_code}
                    onChange={(e) => setForm((f) => ({ ...f, provider_code: e.target.value }))}
                    className={['webhooks-input', theme].join(' ')}
                    placeholder="OMNIVA"
                  />
                </label>

                <label className="webhooks-label">
                  <div className="webhooks-fieldLabel">{t('pricingService')}</div>
                  <input
                    value={form.service_code}
                    onChange={(e) => setForm((f) => ({ ...f, service_code: e.target.value }))}
                    className={['webhooks-input', theme].join(' ')}
                  />
                </label>

                <label className="webhooks-label">
                  <div className="webhooks-fieldLabel">{t('pricingDeliveryType')}</div>
                  <input
                    value={form.delivery_type}
                    onChange={(e) => setForm((f) => ({ ...f, delivery_type: e.target.value }))}
                    className={['webhooks-input', theme].join(' ')}
                  />
                </label>

                <label className="webhooks-label">
                  <div className="webhooks-fieldLabel">{t('pricingCurrency')}</div>
                  <input
                    value={form.currency}
                    onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
                    className={['webhooks-input', theme].join(' ')}
                  />
                </label>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <label className="webhooks-label">
                    <div className="webhooks-fieldLabel">{t('pricingMarkupPercent')}</div>
                    <input
                      type="number"
                      step="any"
                      value={form.markup_percent}
                      onChange={(e) => setForm((f) => ({ ...f, markup_percent: e.target.value }))}
                      className={['webhooks-input', theme].join(' ')}
                    />
                  </label>

                  <label className="webhooks-label">
                    <div className="webhooks-fieldLabel">{t('pricingMarkupFixed')}</div>
                    <input
                      type="number"
                      step="any"
                      value={form.markup_fixed}
                      onChange={(e) => setForm((f) => ({ ...f, markup_fixed: e.target.value }))}
                      className={['webhooks-input', theme].join(' ')}
                    />
                  </label>
                </div>

                <label className="webhooks-label">
                  <div className="webhooks-fieldLabel">{t('pricingMinMargin')}</div>
                  <input
                    type="number"
                    step="any"
                    value={form.min_margin}
                    onChange={(e) => setForm((f) => ({ ...f, min_margin: e.target.value }))}
                    className={['webhooks-input', theme].join(' ')}
                  />
                </label>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <label className="webhooks-label">
                    <div className="webhooks-fieldLabel">{t('pricingRoundingMode')}</div>
                    <input
                      value={form.rounding_mode}
                      onChange={(e) => setForm((f) => ({ ...f, rounding_mode: e.target.value }))}
                      className={['webhooks-input', theme].join(' ')}
                      placeholder="UP"
                    />
                  </label>

                  <label className="webhooks-label">
                    <div className="webhooks-fieldLabel">{t('pricingRoundingPrecision')}</div>
                    <input
                      type="number"
                      step="any"
                      value={form.rounding_precision}
                      onChange={(e) => setForm((f) => ({ ...f, rounding_precision: e.target.value }))}
                      className={['webhooks-input', theme].join(' ')}
                    />
                  </label>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <label className="webhooks-label">
                    <div className="webhooks-fieldLabel">{t('pricingVatPercent')}</div>
                    <input
                      type="number"
                      step="any"
                      value={form.vat_rate_percent}
                      onChange={(e) => setForm((f) => ({ ...f, vat_rate_percent: e.target.value }))}
                      className={['webhooks-input', theme].join(' ')}
                    />
                  </label>

                  <label className="webhooks-label">
                    <div className="webhooks-fieldLabel">{t('pricingTaxPercent')}</div>
                    <input
                      type="number"
                      step="any"
                      value={form.tax_rate_percent}
                      onChange={(e) => setForm((f) => ({ ...f, tax_rate_percent: e.target.value }))}
                      className={['webhooks-input', theme].join(' ')}
                    />
                  </label>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <label className="webhooks-label">
                    <div className="webhooks-fieldLabel">{t('pricingPriority')}</div>
                    <input
                      type="number"
                      step="1"
                      value={form.priority}
                      onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
                      className={['webhooks-input', theme].join(' ')}
                    />
                  </label>

                  <label className="webhooks-label" style={{ display: 'flex', alignItems: 'flex-end', gap: 8, paddingBottom: 2 }}>
                    <input
                      type="checkbox"
                      checked={Boolean(form.tax_included)}
                      onChange={(e) => setForm((f) => ({ ...f, tax_included: e.target.checked }))}
                    />
                    <span>{t('pricingTaxIncluded')}</span>
                  </label>
                </div>

                <label className="webhooks-label" style={{ gridColumn: '1 / -1' }}>
                  <div className="webhooks-fieldLabel">{t('pricingConditions')}</div>
                  <textarea
                    value={form.conditions_text}
                    onChange={(e) => setForm((f) => ({ ...f, conditions_text: e.target.value }))}
                    className={['webhooks-input', 'webhooks-textarea', theme].join(' ')}
                    rows={4}
                    placeholder="{}"
                  />
                </label>

                <label className="webhooks-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={Boolean(form.is_active)}
                    onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                  />
                  <span>{t('pricingActive')}</span>
                </label>
              </div>
              </div>

              <div className="webhooks-modalFooter">
                {editMode === 'edit' && isAdmin && (
                  <button
                    type="button"
                    onClick={onRemove}
                    disabled={saving}
                    className={[
                      'webhooks-dangerBtn',
                      theme,
                      saving ? 'disabled' : 'enabled',
                    ].join(' ')}
                  >
                    {t('Delete')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={closeEdit}
                  disabled={saving}
                  className={[
                    'webhooks-secondaryBtn',
                    theme,
                    saving ? 'disabled' : 'enabled',
                  ].join(' ')}
                >
                  {t('Cancel')}
                </button>
                <button
                  type="button"
                  onClick={onSave}
                  disabled={saving || !canSaveForm}
                  className={[
                    'webhooks-primaryBtn',
                    theme,
                    saving || !canSaveForm ? 'disabled' : 'enabled',
                  ].join(' ')}
                >
                  {saving ? t('Saving') : t('save')}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
